import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Group,
  Text,
  Center,
  Menu,
  Button,
  ThemeIcon,
} from "@mantine/core";
import {
  IconChartBar,
  IconStethoscope,
  IconActivity,
  IconReceipt,
  IconAlertCircle,
  IconChevronDown,
  IconCheck,
  IconScale,
  IconFlask,
  IconClock,
  IconFileInvoice,
  IconListDetails,
  IconClipboardList,
} from "@tabler/icons-react";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useTaskStore } from "../../store/task.store";
import { WorkloadReportContent } from "./WorkloadReportPage";
import { CptFrequencyReportContent } from "./CptFrequencyReportContent";
import { CancerCaseReportContent } from "./CancerCaseReportPage";
import { BillingStatusReportContent } from "./BillingStatusReportPage";
import { PayablesReportContent } from "./PayablesReportPage";
import { TurnaroundTimeReportContent } from "./TurnaroundTimeReportPage";
import { QaMetricsContent } from "../workflows/QaMetricsPage";
import { StainMetricsContent } from "../workflows/StainMetricsPage";
import { ManualTebraEntryReportContent } from "./ManualTebraEntryReportPage";
import { SpecimenLogReportContent } from "./SpecimenLogReportContent";
import { AccessionLogReportContent } from "./AccessionLogReportContent";

type ReportKey =
  | "workload"
  | "cpt-frequency"
  | "specimen-log"
  | "accession-log"
  | "cancer-cases"
  | "billing-status"
  | "payables"
  | "turnaround-time"
  | "qa-metrics"
  | "stain-metrics"
  | "manual-tebra-entry";

interface ReportConfig {
  value: ReportKey;
  label: string;
  icon: React.ReactNode;
  color: string;
  permission: string;
}

interface PathologistReportConfig {
  value: ReportKey;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const REPORTS: ReportConfig[] = [
  {
    value: "workload",
    label: "Workload / Productivity",
    icon: <IconChartBar size={14} />,
    color: "blue",
    permission: "reports.workload.read",
  },
  {
    value: "cpt-frequency",
    label: "CPT Code Frequency",
    icon: <IconStethoscope size={14} />,
    color: "violet",
    permission: "reports.cpt_frequency.read",
  },
  {
    value: "specimen-log",
    label: "Specimen Log",
    icon: <IconListDetails size={14} />,
    color: "indigo",
    permission: "reports.cpt_frequency.read",
  },
  {
    value: "accession-log",
    label: "Accession Log",
    icon: <IconClipboardList size={14} />,
    color: "cyan",
    permission: "reports.cpt_frequency.read",
  },
  {
    value: "cancer-cases",
    label: "Cancer Case Count",
    icon: <IconActivity size={14} />,
    color: "red",
    permission: "reports.cancer_cases.read",
  },
  {
    value: "billing-status",
    label: "Billing Status",
    icon: <IconReceipt size={14} />,
    color: "yellow",
    permission: "reports.billing_status.read",
  },
  {
    value: "manual-tebra-entry",
    label: "Manual Tebra Entry",
    icon: <IconFileInvoice size={14} />,
    color: "blue",
    permission: "billing.read",
  },
  {
    value: "payables",
    label: "Payables",
    icon: <IconAlertCircle size={14} />,
    color: "green",
    permission: "reports.payables.read",
  },
  {
    value: "turnaround-time",
    label: "Turnaround Time (TAT)",
    icon: <IconClock size={14} />,
    color: "orange",
    permission: "reports.workload.read",
  },
];

const PATHOLOGIST_REPORTS: PathologistReportConfig[] = [
  {
    value: "qa-metrics",
    label: "QA Metrics",
    icon: <IconScale size={14} />,
    color: "teal",
  },
  {
    value: "stain-metrics",
    label: "Stain Metrics",
    icon: <IconFlask size={14} />,
    color: "cyan",
  },
];

export default function ReportsLandingPage() {
  const { selectedTasks } = useTaskStore();
  // selectedTasks can be undefined before the store hydrates — guard the map so
  // the page does not crash on first render (Sentry GCP-FRONTEND-3H/E).
  const selectedSlugs = (selectedTasks ?? []).map((t) => t.slug);
  const hasPathologistTask =
    selectedSlugs.includes("pathologist-review") ||
    selectedSlugs.includes("admin");

  const hasWorkload = useHasPermission("reports.workload.read");
  const hasCpt = useHasPermission("reports.cpt_frequency.read");
  const hasCancer = useHasPermission("reports.cancer_cases.read");
  const hasBilling = useHasPermission("reports.billing_status.read");
  const hasManualTebra = useHasPermission("billing.read");
  const hasPayables = useHasPermission("reports.payables.read");

  const permMap: Record<string, boolean> = {
    "reports.workload.read": hasWorkload,
    "reports.cpt_frequency.read": hasCpt,
    "reports.cancer_cases.read": hasCancer,
    "reports.billing_status.read": hasBilling,
    "billing.read": hasManualTebra,
    "reports.payables.read": hasPayables,
  };

  const visibleReports = useMemo(
    () => [
      ...REPORTS.filter((r) => permMap[r.permission]),
      ...(hasPathologistTask ? PATHOLOGIST_REPORTS : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasWorkload,
      hasCpt,
      hasCancer,
      hasBilling,
      hasManualTebra,
      hasPayables,
      hasPathologistTask,
    ]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const activeReport = searchParams.get("report") as ReportKey | null;

  const resolvedActive: ReportKey | null =
    activeReport && visibleReports.some((r) => r.value === activeReport)
      ? activeReport
      : visibleReports[0]?.value ?? null;

  const activeConfig = visibleReports.find((r) => r.value === resolvedActive);

  if (visibleReports.length === 0) {
    return (
      <Stack gap="lg" p="md">
        <Text fw={600} size="lg">
          Reports
        </Text>
        <Center py="xl">
          <Text c="dimmed">No reports available for your account.</Text>
        </Center>
      </Stack>
    );
  }

  const mainReports = visibleReports.filter(
    (r) => r.value !== "qa-metrics" && r.value !== "stain-metrics"
  );
  const pathologistReports = visibleReports.filter(
    (r) => r.value === "qa-metrics" || r.value === "stain-metrics"
  );

  return (
    <Stack gap="lg" p="md">
      <Group justify="space-between" align="center">
        <Text
          size="xs"
          c="dimmed"
          tt="uppercase"
          fw={700}
          style={{ letterSpacing: "0.08em" }}
        >
          Reports
        </Text>

        <Menu shadow="md" width={270} position="bottom-end">
          <Menu.Target>
            <Button
              variant="default"
              leftSection={
                activeConfig ? (
                  <ThemeIcon
                    size={20}
                    radius="sm"
                    color={activeConfig.color}
                    variant="light"
                  >
                    {activeConfig.icon}
                  </ThemeIcon>
                ) : undefined
              }
              rightSection={<IconChevronDown size={14} />}
            >
              {activeConfig?.label ?? "Select report"}
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            {mainReports.map((report) => (
              <Menu.Item
                key={report.value}
                leftSection={
                  <ThemeIcon
                    size={22}
                    radius="sm"
                    color={report.color}
                    variant="light"
                  >
                    {report.icon}
                  </ThemeIcon>
                }
                rightSection={
                  resolvedActive === report.value ? (
                    <IconCheck size={14} />
                  ) : undefined
                }
                fw={resolvedActive === report.value ? 600 : 400}
                onClick={() => setSearchParams({ report: report.value })}
              >
                {report.label}
              </Menu.Item>
            ))}

            {pathologistReports.length > 0 && (
              <>
                <Menu.Divider />
                <Menu.Label>Pathologist Reports</Menu.Label>
                {pathologistReports.map((report) => (
                  <Menu.Item
                    key={report.value}
                    leftSection={
                      <ThemeIcon
                        size={22}
                        radius="sm"
                        color={report.color}
                        variant="light"
                      >
                        {report.icon}
                      </ThemeIcon>
                    }
                    rightSection={
                      resolvedActive === report.value ? (
                        <IconCheck size={14} />
                      ) : undefined
                    }
                    fw={resolvedActive === report.value ? 600 : 400}
                    onClick={() => setSearchParams({ report: report.value })}
                  >
                    {report.label}
                  </Menu.Item>
                ))}
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      {resolvedActive === "workload" && <WorkloadReportContent />}
      {resolvedActive === "cpt-frequency" && <CptFrequencyReportContent />}
      {resolvedActive === "specimen-log" && <SpecimenLogReportContent />}
      {resolvedActive === "accession-log" && <AccessionLogReportContent />}
      {resolvedActive === "cancer-cases" && <CancerCaseReportContent />}
      {resolvedActive === "billing-status" && <BillingStatusReportContent />}
      {resolvedActive === "manual-tebra-entry" && (
        <ManualTebraEntryReportContent />
      )}
      {resolvedActive === "payables" && <PayablesReportContent />}
      {resolvedActive === "turnaround-time" && <TurnaroundTimeReportContent />}
      {resolvedActive === "qa-metrics" && <QaMetricsContent />}
      {resolvedActive === "stain-metrics" && <StainMetricsContent />}
    </Stack>
  );
}
